import { KontoausgleicheArt } from '@/domain/mitarbeiter/features/feiertagskonto/entities';
import { KontoausgleichsArtSelection } from '@/domain/mitarbeiter/features/feiertagskonto/features/kontoausgleich/components/KontoausgleichsArtSelection';
import { useCreateKontoausgleichForm } from '@/domain/mitarbeiter/features/feiertagskonto/features/kontoausgleich/useCreateKontoausgleichForm';
import { useIsMobile } from '@/hooks';
import {
  SheetFooter,
  SheetTrigger,
  Button,
  Field,
  FieldDescription,
  FieldLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  TextArea,
  InputDatePicker,
  FieldError,
} from '@e2n/ui/v2';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

export function KontoausgleichForm({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { id } = useParams();
  const { form } = useCreateKontoausgleichForm(id ?? '');

  const roundTag = (tag: number) => {
    return Math.round(tag * 2) / 2;
  };

  const formatTag = (tag: number) => {
    return tag.toLocaleString('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    });
  };
  const parseTag = (tag: string) => {
    const normalize = tag.replace(',', '.');
    const number = Number(normalize);
    if (!Number.isFinite(number)) {
      return '1';
    }
    const rounded = roundTag(number);
    return formatTag(rounded);
  };
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent>
        <form
          id="createFeierktagskontoKontoausgleich"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}>
          <SheetHeader>
            <SheetTitle className="py-2 w-full">
              {isMobile ? (
                <div className="flex items-center justify-between">
                  {t(
                    'domain.mitarbeiter.feiertagskonto.kontoausgleich.form.title',
                  )}
                  <form.Subscribe
                    selector={(state) => [state.canSubmit, state.isSubmitting]}>
                    {([canSubmit, isSubmitting]) => {
                      return (
                        <Button
                          disabled={!canSubmit || isSubmitting}
                          onClick={() => {
                            form.handleSubmit();
                            form.reset();
                            setOpen(false);
                          }}>
                          {!isSubmitting
                            ? t('actions.fertig')
                            : t('actions.loading')}
                        </Button>
                      );
                    }}
                  </form.Subscribe>
                </div>
              ) : (
                <div>
                  {t(
                    'domain.mitarbeiter.feiertagskonto.kontoausgleich.form.title',
                  )}
                </div>
              )}
            </SheetTitle>
          </SheetHeader>
          <SheetBody className="flex flex-col gap-4 p-6">
            <span className="font-normal text-neutrals-700 text-xs">
              {t(
                'domain.mitarbeiter.feiertagskonto.kontoausgleich.form.pflicht',
              )}
            </span>
            <form.AppField name="art">
              {(field) => (
                <Field>
                  <FieldLabel requiredField>
                    {t('domain.mitarbeiter.feiertagskonto.kontoausgleich.art')}
                  </FieldLabel>
                  <Select
                    name={field.name}
                    value={field.state.value}
                    onValueChange={(value: KontoausgleicheArt) =>
                      field.handleChange(value)
                    }>
                    <SelectTrigger id={field.name} onBlur={field.handleBlur}>
                      <SelectValue>
                        <KontoausgleichsArtSelection
                          value={field.state.value}
                        />
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(KontoausgleicheArt).map((enumValue) => (
                        <SelectItem
                          key={enumValue}
                          value={enumValue}
                          onChange={() => field.handleChange(enumValue)}>
                          {<KontoausgleichsArtSelection value={enumValue} />}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {field.state.value ===
                  KontoausgleicheArt.FEIERTAGSAUSZAHLUNG ? (
                    <FieldDescription>
                      {t(
                        'domain.mitarbeiter.feiertagskonto.kontoausgleich.form.artInfoAuszahlung',
                      )}
                    </FieldDescription>
                  ) : (
                    <FieldDescription>
                      {t(
                        'domain.mitarbeiter.feiertagskonto.kontoausgleich.form.artInfoKorrektur',
                      )}
                    </FieldDescription>
                  )}
                </Field>
              )}
            </form.AppField>
            <form.AppField name="datum">
              {(field) => (
                <Field>
                  <FieldLabel requiredField>
                    {t(
                      'domain.mitarbeiter.feiertagskonto.kontoausgleich.datum',
                    )}
                  </FieldLabel>
                  <InputDatePicker
                    id={field.name}
                    value={
                      field.state.value
                        ? dayjs(field.state.value).toDate()
                        : undefined
                    }
                    onChange={(d) => field.handleChange(dayjs(d).toISOString())}
                    onBlur={field.handleBlur}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.AppField>
            <form.AppField name="tage">
              {(field) => (
                <Field>
                  <FieldLabel requiredField>
                    {t(
                      'domain.mitarbeiter.feiertagskonto.kontoausgleich.form.anzahl',
                    )}
                  </FieldLabel>
                  <Input
                    id={field.name}
                    step=".5"
                    type="number"
                    value={field.state.value ?? ''}
                    inputMode="decimal"
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                    }}
                    onBlur={(e) => {
                      field.handleChange(parseTag(e.target.value));
                      field.handleBlur();
                    }}
                    showSuffix
                    suffix={t(
                      'domain.mitarbeiter.feiertagskonto.kontoausgleich.tage',
                    )}
                  />
                  <FieldError errors={field.state.meta.errors} />
                </Field>
              )}
            </form.AppField>
            <form.AppField name={'bemerkung'}>
              {(field) => (
                <Field>
                  <FieldLabel>
                    {t(
                      'domain.mitarbeiter.feiertagskonto.kontoausgleich.bemerkung',
                    )}
                  </FieldLabel>
                  <TextArea
                    id={field.name}
                    value={field.state.value ?? ''}
                    maxLength={250}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </Field>
              )}
            </form.AppField>
          </SheetBody>
          {!isMobile && (
            <SheetFooter className="flex p-6 ">
              <form.Subscribe
                selector={(state) => [state.canSubmit, state.isSubmitting]}>
                {([canSubmit, isSubmitting]) => {
                  return (
                    <Button
                      disabled={!canSubmit || isSubmitting}
                      onClick={() => {
                        form.handleSubmit();
                        form.reset();
                        setOpen(false);
                      }}>
                      {!isSubmitting
                        ? t('actions.fertig')
                        : t('actions.loading')}
                    </Button>
                  );
                }}
              </form.Subscribe>
            </SheetFooter>
          )}
        </form>
      </SheetContent>
    </Sheet>
  );
}
